import { Fragment, h } from "preact";
import { RoutableProps, useRouter } from "preact-router";
import { useCallback, useEffect, useState } from "preact/hooks";
import { Button, Container, FormField, Spinner } from "uitoolkit";
import { resetPassword, validateOnetimeToken } from "services/backend";
import { translate } from "services/translations";
import { usePageTitle } from "hooks";

// todo: consider showing a countdown before expiration .. like the circle countdown on mbpay

enum State {
    Pending,
    NotFound,
    Expired,
    Valid,
    Done,
    Error
}

export const ResetPasswordbox = (props: RoutableProps) => {
    usePageTitle(translate("changepassword_pagetitle"));

    const [state, setState] = useState<State>(State.Valid); // State.Pending
    const [code, setCode] = useState("123123"); // ""
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [password2, setPassword2] = useState("");
    const [showValidation, setShowValidation] = useState(false);
    const [waiting, setWaiting] = useState(false);

    const passwordCallback = useCallback((e: h.JSX.TargetedEvent<HTMLInputElement, Event>) => setPassword(e.currentTarget.value), [setPassword]);
    const password2Callback = useCallback((e: h.JSX.TargetedEvent<HTMLInputElement, Event>) => setPassword2(e.currentTarget.value), [setPassword2]);

    const [ctx] = useRouter();
    useEffect(() => {
        // const token = base64Decode(ctx.matches?.token ?? "");
        const token = ctx.matches?.token;
        if (!token) {
            setState(State.NotFound);
            return;
        }
        //console.log("TOKEN: " + token);
        validateOnetimeToken(token)
            .then(res => {
                if (res?.data?.user.validateOnetimeToken.ok) {
                    setCode(token);
                    setEmail(res!.data.user.validateOnetimeToken.email ?? "");
                    setState((res!.data.user.validateOnetimeToken!.expirationInSeconds ?? 0) > 0 ? State.Valid : State.Expired);
                }
                else {
                    setState(State.Expired);
                }
            })
    }, [ctx.matches?.token])

    const onSubmit = useCallback(async () => {
        if (!code || state !== State.Valid) {
            return;
        }
        setShowValidation(true);
        if (!password || !password2 || password !== password2) {
            //setFeedback(translate("login_changepassword_feedback_passwords_missing_or_not_identical"));
            return;
        }
        setShowValidation(false);
        setWaiting(true);
        //setFeedback("");

        const res = await resetPassword(code, password);
        setWaiting(false);
        if (!res?.data?.user.resetPassword?.ok) {
            // todo: warning, email not found
            return;
        }
        setState(State.Done);
    }, [password, password2, code, state])

    const handleEnter = useCallback((e: KeyboardEvent) => {
        if (e.key == "Enter")
            onSubmit();
    }, [onSubmit]);

    return (
        <Container class="component-form flex-column gap-16" onKeyUp={handleEnter}>
            {
                // Show a spinner while we validate the token
                state === State.Pending && <Fragment>
                    <div class="flex-column center">
                        <Spinner class="small" />
                    </div>
                </Fragment>
            }
            {
                // Show an error if the token was invalid
                state === State.NotFound && <Fragment>
                    <h1>{translate("changepassword_headline_not_found")}</h1>
                    <h2>{translate("changepassword_description_not_found")}</h2>
                </Fragment>
            }
            {
                // Show an error if the token was expired
                state === State.Expired && <Fragment>
                    <h1>{translate("changepassword_headline_expired")}</h1>
                    <h2>{translate("changepassword_description_expired")}</h2>
                </Fragment>
            }
            {
                // Show the UI for setting a new password, since the token was valid
                state === State.Valid && <Fragment>
                    <h1>{translate("changepassword_headline")}</h1>

                    <FormField
                        id="password"
                        type="password"
                        label={translate("changepassword_hint_new_password")}
                        placeholder=""
                        value={password}
                        autoComplete="new-password"
                        disabled={waiting}
                        state={!showValidation ? undefined : (password.length < 6 ? "invalid" : "valid")}
                        onChange={passwordCallback} />
                    <FormField
                        id="repeatpassword"
                        type="password"
                        label={translate("changepassword_hint_repeat_password")}
                        placeholder=""
                        value={password2}
                        autoComplete="new-password"
                        disabled={waiting}
                        state={!showValidation ? undefined : (password2.length < 6 || password2 !== password ? "invalid" : "valid")}
                        onChange={password2Callback} />
                    <Button
                        variant="primary"
                        size="large"
                        disabled={waiting}
                        class="wide"
                        onClick={onSubmit}>{translate("changepassword_button_set_password")}</Button>
                </Fragment>
            }
            {
                // Confirm that the password was changed
                state === State.Done && <Fragment>
                    <h2>{translate("changepassword_headline_password_updated")}</h2>
                </Fragment>
            }
            {
                // There was a problem setting the new password
                state === State.Error && <Fragment>
                    <h2>{translate("changepassword_headline_error")}</h2>
                </Fragment>
            }
        </Container>
    );
};
